import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material";

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    darkPurple: {
      100: "#B5A1B9",
      200: "#754E7D",
      300: "#5B4260",
      400: "#402147",
      500: "#321338",
    },
    lightPurple: {
      100: "#F9F8FF",
      200: "#ECEAFF",
      300: "#DED4F6",
      400: "#CDBCF3",
      500: "#9584BA",
    },
    lime: {
      100: "#DEFB1E",
      200: "#C5E200",
      300: "#819400",
      400: "#586600",
    },
    beige: {
      100: "#FFF",
      200: "#F7F4E7",
      300: "#EBE6D2",
      400: "#DDD9C7",
      500: "#C3BDA1",
      600: "#A8A289",
      700: "#8E8970",
      800: "#7E785E",
    },
    gray: {
      100: "#F9F9F9",
      200: "#F1F1F1",
      300: "#9E9E9E",
      400: "#818181",
    },
    state: {
      100: "#18DC81",
      200: "#F53D49",
      300: "#9E9E9E 50%",
    },
    primary: {
      main: "rgba(50, 19, 56, 1)", // dark-purple1
      light: "rgba(117, 78, 128, 1)", // dark-purple2
      contrastText: "rgba(205, 188, 243, 1)", // beige-1
    },
    secondary: {
      main: "rgba(222, 251, 30, 1)", // lime-1
    },
    error: {
      main: "rgba(220, 27, 36, 1)", // example error color
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF"  // beige-2
    },
    text: {
      primary: "#321338", // beige-1
      secondary: "#9584BA",
      disabled: "rgba(184, 184, 184, 1)", // gray-3
    }
  },
  typography: {
    fontFamily: "Manrope, Helvetica",
    h1: {
      fontSize: "48px",
      "@media (max-width:992px)": {
        fontSize: "36px", // 48px
      },
      fontWeight: 500,
      letterSpacing: "-2%",
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "60px",
      "@media (max-width:992px)": {
        fontSize: "48px !important",
      },
      fontWeight: 300,
      letterSpacing: "2%",
      lineHeight: 1.2,
    },
    h3: {
      fontSize: "72px",
      "@media (max-width:992px)": {
        fontSize: "48px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h4: {
      fontSize: "60px",
      "@media (max-width:992px)": {
        fontSize: "28px !important",
      },
      fontWeight: 300,
      letterSpacing: "1.2px",
      lineHeight: 1.2,
    },
    h5: {
      fontSize: "40px",
      "@media (max-width:992px)": {
        fontSize: "18px !important",
      },
      fontWeight: 300,
      lineHeight: 1.2,
    },
    h6: {
      fontSize: "72px",
      "@media (max-width:1192px)": {
        fontSize: "54px",
      },
      "@media (max-width:992px)": {
        fontSize: "36px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle1: { //subheading-1
      fontSize: "22px",
      "@media (max-width:992px)": {
        fontSize: "16px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle2: { //subheading-2
      fontSize: "22px",
      "@media (max-width:992px)": {
        fontSize: "18px",
      },
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle3: { //subheading-3
      fontSize: "24px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle4: { //subheading-4
      fontSize: "28px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle5: {  //subheading-5
      fontSize: "28px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    subtitle6: {  //subheading-6} 
      fontSize: "30px",
      "@media (max-width:992px)": {
        fontSize: "24px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle7: {
      fontSize: "32px",
      "@media (max-width:992px)": {
        fontSize: "28px",
      },
      fontWeight: 400,
      lineHeight: 1.2,
    },
    subtitle8: {
      fontSize: "36px",
      "@media (max-width:992px)": {
        fontSize: "28px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle9: {
      fontSize: "38px",
      "@media (max-width:992px)": {
        fontSize: "30px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle10: {
      fontSize: "40px",
      "@media (max-width:992px)": {
        fontSize: "28px",
      },
      fontWeight: 400,
      lineHeight: 1.2,
    },
    subtitle11: { //subheading-3
      fontSize: "22px",
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle12: { 
      fontSize: "28px",
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "uppercase",
    },
    subtitle13: { 
      fontSize: "36px",
      "@media (max-width:992px)": {
        fontSize: "24px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "uppercase",
    },
    subtitle14: { 
      fontSize: "36px",
      "@media (max-width:1192px)": {
        fontSize: "28px",
      },
      "@media (max-width:992px)": {
        fontSize: "20px",
      },
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "uppercase",
    },
    body1: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body2: {
      fontSize: "13px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        fontWeight: 600,
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body3: {
      fontSize: "13px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        lineHeight: 1.42,
      },
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body4: {
      fontSize: "16px",
      "@media (max-width:992px)": {
        fontSize: "12px",
        lineHeight: 1.42,
      },
      fontWeight: 500,
      lineHeight: 1.42,
    },
    body5: {
      fontSize: "16px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        lineHeight: 1.42,
      },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    body6: {
      fontSize: "18px",
      "@media (max-width:992px)": {
        fontSize: "14px",
        lineHeight: 1.42,
      },
      fontWeight: 600,
      lineHeight: 1.2,
    },
    body7: {
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: 1.42,
    },
    body8: {
      fontSize: "18px",
      "@media (max-width:992px)": {
        fontSize: "16px",
      },
      fontWeight: 600,
      lineHeight: "normal",
    },
    button: {
      fontSize: "16px", // 16px
      "@media (max-width:992px)": {
        fontSize: "14px", // 18px
      },
      fontWeight: 600,
      lineHeight: "normal",
      textTransform: "none",
    },
    caption: {
      fontSize: "13px", // 12px
      fontWeight: 500,
      lineHeight: 1.2,
      fontStyle: "normal",
    },
    overline: {
      fontSize: "0.8125rem", // 13px
      fontWeight: 500,
      lineHeight: 1.2,
    },
  },
});

import { ReactNode } from "react";

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};
